import { useMemo, useState } from 'react';
import { useApp } from '../context.js';
import { yearRecap, recapYears } from '../lib/stats.js';
import { cuisineLabel } from '../lib/categories.js';
import { formatLocalDate } from '../lib/date.js';
import { shareRecap } from '../lib/recapCanvas.js';

// "Our Year in Restaurants": pick a year (or all time), see the highlights, share them as an image.
export default function RecapCard() {
    const { colorTheme, currentProfile, haptic, restaurants, showToast, t } = useApp();
    const years = useMemo(() => recapYears(restaurants), [restaurants]);
    const [year, setYear] = useState(() => years[0] || String(new Date().getFullYear()));
    const recap = useMemo(() => yearRecap(restaurants, year === 'all' ? 'all' : Number(year)), [restaurants, year]);
    const [busy, setBusy] = useState(false);
    if (!years.length) return null;

    const themeKey = colorTheme === 'classic' ? 'classic' : currentProfile === 'les' ? 'les' : 'doug';
    const cb = recap.checkBattle;
    const share = async () => {
        setBusy(true);
        haptic('medium');
        try {
            const how = await shareRecap(recap, themeKey);
            if (how === 'downloaded') showToast('Recap image saved 📸', 'success');
        } catch (e) {
            showToast("Couldn't make the image", 'error');
        } finally {
            setBusy(false);
        }
    };

    const line = (emoji, label, value) => value && (
        <div className="flex items-start gap-3">
            <span className="text-2xl leading-none">{emoji}</span>
            <div className="min-w-0">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</div>
                <div className="text-base font-semibold text-gray-800">{value}</div>
            </div>
        </div>
    );

    return (
        <div className="bg-white/90 apple-card rounded-2xl p-5 shadow-md">
            <div className="flex items-center justify-between gap-2 mb-4">
                <h2 className="text-xl font-bold text-gray-800">💖 Our Year in Restaurants</h2>
            </div>
            <div className="flex gap-2 mb-4 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {[...years, 'all'].map(y => (
                    <button
                        key={y}
                        onClick={() => setYear(y)}
                        aria-pressed={year === y}
                        className={`px-3 py-2 rounded-full text-sm font-semibold apple-button whitespace-nowrap ${year === y ? `bg-gradient-to-r ${t.accent} text-white` : 'bg-gray-100 text-gray-600'}`}
                    >
                        {y === 'all' ? 'All time' : y}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className={`${t.accentBg} rounded-xl py-3`}>
                    <div className={`text-2xl font-bold ${t.accentText}`}>{recap.placesTried}</div>
                    <div className="text-xs text-gray-600">places tried</div>
                </div>
                <div className={`${t.accentBg} rounded-xl py-3`}>
                    <div className={`text-2xl font-bold ${t.accentText}`}>{recap.nightsOut}</div>
                    <div className="text-xs text-gray-600">nights out</div>
                </div>
                <div className={`${t.accentBg} rounded-xl py-3`}>
                    <div className={`text-2xl font-bold ${t.accentText}`}>{recap.checks ? `$${Math.round(recap.totalSpent)}` : '—'}</div>
                    <div className="text-xs text-gray-600">on checks</div>
                </div>
            </div>

            <div className="space-y-3 mb-5">
                {line('⭐', 'Our favorite', recap.favoritePlace && `${recap.favoritePlace.name} · ${recap.favoritePlace.rating}/10`)}
                {line('🥂', 'Best date night', recap.bestDateNight && `${recap.bestDateNight.name} · ${formatLocalDate(recap.bestDateNight.date, { month: 'short', day: 'numeric' })} · 💛 ${recap.bestDateNight.doug} 💖 ${recap.bestDateNight.les}`)}
                {line('🏆', 'Check Battle champ', cb.winner && `${cb.winner === 'doug' ? '💛 Doug' : cb.winner === 'les' ? '💖 Les' : '🤝 A tie'} · ${cb.doug}–${cb.les}${cb.ties ? ` (${cb.ties} tie${cb.ties === 1 ? '' : 's'})` : ''}`)}
                {line('🍽️', 'Our go-to', recap.topCuisine && cuisineLabel(recap.topCuisine.id))}
                {line('🎯', 'Closest guess', recap.closestGuess && `${recap.closestGuess.who === 'doug' ? 'Doug' : 'Les'} · $${recap.closestGuess.diff.toFixed(2)} off at ${recap.closestGuess.name}`)}
            </div>

            <button
                onClick={share}
                disabled={busy}
                className={`w-full bg-gradient-to-r ${t.accent} text-white px-4 py-3 rounded-xl font-semibold apple-button disabled:opacity-60`}
            >
                {busy ? 'Making the image…' : 'Share as image 📤'}
            </button>
        </div>
    );
}
